/**
 * E2E client skeleton: X25519 ECDH + HKDF-SHA256 + AES-256-GCM envelopes, plus
 * Ed25519 approval signatures.
 *
 * The Worker only ever routes `ciphertext`; the shared key is derived from the
 * X25519 key exchange performed at pairing time (see docs/PROTOCOL-v0.md §3/§5).
 */
import type { ApprovalDecision, Envelope, MessageType } from '@luminis/protocol';
import { makeEnvelope } from '@luminis/protocol';
import { ed25519, x25519 } from '@noble/curves/ed25519';
import { base64UrlDecode, base64UrlEncode, nowSeconds, randomBytes, randomId } from '../id';

export interface KeyPairB64 {
  privateKey: string;
  publicKey: string;
}

export function generateExchangeKeyPair(): KeyPairB64 {
  const privateKey = x25519.utils.randomPrivateKey();
  return {
    privateKey: base64UrlEncode(privateKey),
    publicKey: base64UrlEncode(x25519.getPublicKey(privateKey)),
  };
}

export function generateSigningKeyPair(): KeyPairB64 {
  const privateKey = ed25519.utils.randomPrivateKey();
  return {
    privateKey: base64UrlEncode(privateKey),
    publicKey: base64UrlEncode(ed25519.getPublicKey(privateKey)),
  };
}

const HKDF_SALT = new TextEncoder().encode('luminis-e2e-v1');
const HKDF_INFO = new TextEncoder().encode('luminis aes-256-gcm device channel');

/** Derive the channel key from our private X25519 key and the peer's public key. */
export async function deriveSharedKey(
  privateKeyB64: string,
  peerPublicKeyB64: string,
): Promise<CryptoKey> {
  const privateKey = base64UrlDecode(privateKeyB64);
  const peerPublicKey = base64UrlDecode(peerPublicKeyB64);
  if (privateKey.length !== 32) throw new Error('X25519 private key must be 32 bytes');
  if (peerPublicKey.length !== 32) throw new Error('X25519 public key must be 32 bytes');
  const shared = x25519.getSharedSecret(privateKey, peerPublicKey);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    shared as unknown as BufferSource,
    'HKDF',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: HKDF_INFO },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export function envelopeAad(type: MessageType, deviceId?: string): ArrayBuffer {
  const bytes = new TextEncoder().encode(`luminis:v1:${type}:${deviceId ?? ''}`);
  return bytes.buffer as ArrayBuffer;
}

export interface EncryptEnvelopeOptions {
  type: MessageType;
  payload: unknown;
  deviceId?: string;
  id?: string;
  ts?: number;
  key: CryptoKey;
}

export async function encryptEnvelope(options: EncryptEnvelopeOptions): Promise<Envelope> {
  const nonce = randomBytes(12);
  const plaintext = new TextEncoder().encode(JSON.stringify(options.payload));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce as unknown as BufferSource,
      additionalData: envelopeAad(options.type, options.deviceId),
    },
    options.key,
    plaintext,
  );
  return makeEnvelope(options.type, {
    id: options.id ?? randomId('evt_'),
    ts: options.ts ?? nowSeconds(),
    ...(options.deviceId ? { device_id: options.deviceId } : {}),
    nonce: base64UrlEncode(nonce),
    ciphertext: base64UrlEncode(new Uint8Array(ciphertext)),
  });
}

export async function decryptEnvelope<T = unknown>(key: CryptoKey, envelope: Envelope): Promise<T> {
  if (!envelope.ciphertext || !envelope.nonce) {
    throw new Error('Envelope is not E2E encrypted');
  }
  const nonce = base64UrlDecode(envelope.nonce);
  if (nonce.length !== 12) throw new Error('Invalid nonce length');
  const ciphertext = base64UrlDecode(envelope.ciphertext);
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce as unknown as BufferSource,
      additionalData: envelopeAad(envelope.type, envelope.device_id),
    },
    key,
    ciphertext as unknown as BufferSource,
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

/** Replay protection: one-time nonces inside a ±5 minute window. */
export class NonceCache {
  private readonly seen = new Map<string, number>();
  private readonly windowSeconds: number;
  private readonly maxEntries: number;
  private readonly clock: () => number;

  constructor(options: { windowSeconds?: number; maxEntries?: number; clock?: () => number } = {}) {
    this.windowSeconds = options.windowSeconds ?? 300;
    this.maxEntries = options.maxEntries ?? 500;
    this.clock = options.clock ?? (() => nowSeconds());
  }

  accept(nonce: string, ts: number): boolean {
    if (typeof nonce !== 'string' || nonce.length === 0) return false;
    const now = this.clock();
    if (!Number.isFinite(ts) || Math.abs(now - ts) > this.windowSeconds) return false;
    if (this.seen.has(nonce)) return false;
    this.seen.set(nonce, ts);
    if (this.seen.size > this.maxEntries) {
      const oldest = this.seen.keys().next();
      if (!oldest.done) this.seen.delete(oldest.value);
    }
    return true;
  }
}

// ---------------------------------------------------------------------------
// Approval signatures
// ---------------------------------------------------------------------------

export interface ApprovalSignatureInput {
  approval_id: string;
  decision: ApprovalDecision;
  args_hash: string;
  nonce: string;
}

/**
 * Canonical message covered by the approval signature.
 *
 * MUST match the daemon/helper wire contract byte for byte:
 * - `apps/server/src/security/signing.ts` -> `approvalSignaturePayload`
 * - `apps/helper/src/protocol.ts` -> `approvalSignaturePayload`
 *
 * Format: `${approval_id}\n${decision}\n${args_hash}\n${nonce}` (UTF-8, no
 * trailing newline). Pinned by a fixed-vector test to stop cross-end drift.
 */
export function approvalSigningMessage(input: ApprovalSignatureInput): string {
  return `${input.approval_id}\n${input.decision}\n${input.args_hash}\n${input.nonce}`;
}

/**
 * Standard (padded) base64 for approval signatures.
 *
 * The daemon's Ed25519 helpers emit standard base64 and the admin helper's
 * verifier only accepts the standard alphabet (`/^[A-Za-z0-9+/]+={0,2}$/`);
 * base64url output would be rejected for ~93% of signatures.
 */
function standardBase64Encode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function newApprovalNonce(): string {
  return base64UrlEncode(randomBytes(16));
}

/**
 * Generic Ed25519 signature over a UTF-8 message using the **standard** base64
 * alphabet (needed for M1.1 session.command signatures, which are verified with
 * the daemon's standard-base64 parser).
 */
export async function signStandardBase64(
  signingPrivateKeyB64: string,
  message: string,
): Promise<string> {
  const privateKey = base64UrlDecode(signingPrivateKeyB64);
  if (privateKey.length !== 32) throw new Error('Ed25519 private key must be 32 bytes');
  return standardBase64Encode(ed25519.sign(new TextEncoder().encode(message), privateKey));
}

export async function signApproval(
  signingPrivateKeyB64: string,
  input: ApprovalSignatureInput,
): Promise<string> {
  const privateKey = base64UrlDecode(signingPrivateKeyB64);
  if (privateKey.length !== 32) throw new Error('Ed25519 private key must be 32 bytes');
  const signature = ed25519.sign(
    new TextEncoder().encode(approvalSigningMessage(input)),
    privateKey,
  );
  return standardBase64Encode(signature);
}

/** Generic Ed25519 signature over a UTF-8 message (pairing transcript etc.). */
export async function signMessage(signingPrivateKeyB64: string, message: string): Promise<string> {
  const privateKey = base64UrlDecode(signingPrivateKeyB64);
  if (privateKey.length !== 32) throw new Error('Ed25519 private key must be 32 bytes');
  return base64UrlEncode(ed25519.sign(new TextEncoder().encode(message), privateKey));
}

export async function verifyMessage(
  signingPublicKeyB64: string,
  message: string,
  signatureB64: string,
): Promise<boolean> {
  try {
    const publicKey = base64UrlDecode(signingPublicKeyB64);
    const signature = base64UrlDecode(signatureB64);
    if (publicKey.length !== 32 || signature.length !== 64) return false;
    return ed25519.verify(signature, new TextEncoder().encode(message), publicKey);
  } catch {
    return false;
  }
}

export async function verifyApproval(
  signingPublicKeyB64: string,
  input: ApprovalSignatureInput,
  signatureB64: string,
): Promise<boolean> {
  try {
    const publicKey = base64UrlDecode(signingPublicKeyB64);
    const signature = base64UrlDecode(signatureB64);
    if (publicKey.length !== 32 || signature.length !== 64) return false;
    return ed25519.verify(
      signature,
      new TextEncoder().encode(approvalSigningMessage(input)),
      publicKey,
    );
  } catch {
    return false;
  }
}

export function fingerprint(base64Key: string, groups = 4): string {
  const hex = [...base64UrlDecode(base64Key)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  const chunks = hex.match(/.{1,4}/gu) ?? [];
  return chunks.slice(0, groups).join(' ');
}
